<template>
  <div :class="notificationContainerClass">
    <TransitionGroup name="notification" tag="div">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="[
          'notification',
          `notification--${notification.type}`,
          { 'notification--mobile': isMobile }
        ]"
      >
        <div class="notification__content">
          <div class="notification__header">
            <Icon 
              :icon="getNotificationIcon(notification.type)" 
              class="notification__icon" 
            />
            <h4 class="notification__title">{{ notification.title }}</h4>
            <button
              @click="removeNotification(notification.id)"
              class="notification__close"
              aria-label="Close notification"
            >
              <Icon icon="mdi:close" />
            </button>
          </div>
          <p class="notification__message">{{ notification.message }}</p>
          <div v-if="notification.actions?.length" class="notification__actions">
            <button
              v-for="action in notification.actions"
              :key="action.label"
              @click="action.action"
              class="notification__action"
            >
              {{ action.label }}
            </button>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useNotifications } from '@/composables/ui/useNotifications';

export default {
  name: 'NotificationSystem',
  components: {
    Icon
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { notifications, removeNotification, getNotificationIcon } = useNotifications();

    const notificationContainerClass = computed(() => ({
      'notifications': true,
      'notifications--mobile': props.isMobile,
      'notifications--desktop': !props.isMobile
    }));

    return {
      notifications,
      removeNotification,
      getNotificationIcon,
      notificationContainerClass
    };
  }
};
</script>

<style scoped>
/* Desktop Notifications */
.notifications--desktop {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  max-width: 400px;
  width: 100%;
}

/* Mobile Notifications */
.notifications--mobile {
  position: fixed;
  top: 60px;
  left: 20px;
  right: 20px;
  z-index: 999999;
}

.notification {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  margin-bottom: 12px;
  border-left: 4px solid #4B5563;
  overflow: hidden;
}

.notification--mobile {
  border-left: 4px solid #374151;
}

.notification--info {
  border-left-color: #4B5563;
}

.notification--success {
  border-left-color: #10B981;
}

.notification--warning {
  border-left-color: #F59E0B;
}

.notification--error {
  border-left-color: #EF4444;
}

.notification__content {
  padding: 16px;
}

.notification__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.notification__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.notification--info .notification__icon {
  color: #4B5563;
}

.notification--success .notification__icon {
  color: #10B981;
}

.notification--warning .notification__icon {
  color: #F59E0B;
}

.notification--error .notification__icon {
  color: #EF4444;
}

.notification__title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  flex: 1;
}

.notification__close {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.notification__close:hover {
  background: #F3F4F6;
  color: #111827;
}

.notification__close:active {
  background: #E5E7EB;
  transform: scale(0.95);
}

.notification__message {
  font-size: 13px;
  color: #6B7280;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.notification__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.notification__action {
  padding: 6px 12px;
  background: #4B5563;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification__action:hover {
  background: #374151;
}

.notification__action:active {
  transform: scale(0.95);
}

/* Transitions */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

/* Mobile-specific adjustments */
@media (max-width: 480px) {
  .notifications--mobile {
    top: 10px;
    left: 10px;
    right: 10px;
  }
  
  .notification {
    margin-bottom: 8px;
  }
  
  .notification__content {
    padding: 12px;
  }
}

/* Tablet notifications */
@media (min-width: 481px) and (max-width: 1024px) {
  .notifications--desktop {
    top: 20px;
    right: 20px;
    max-width: 450px;
  }
  
  .notification__title {
    font-size: 15px;
  }
  
  .notification__message {
    font-size: 14px;
  }
}

/* Landscape orientation */
@media (orientation: landscape) and (max-height: 600px) {
  .notifications--desktop {
    top: 8px;
    right: 8px;
    max-width: 350px;
  }
  
  .notifications--mobile {
    top: 8px;
    left: 8px;
    right: 8px;
  }
  
  .notification {
    margin-bottom: 6px;
  }
  
  .notification__content {
    padding: 10px 12px;
  }
  
  .notification__title {
    font-size: 13px;
  }
  
  .notification__message {
    font-size: 12px;
    margin-bottom: 8px;
  }
  
  .notification__header {
    margin-bottom: 6px;
  }
}
</style> 